import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import BookMyOpAPI from '../services/BookMyOpapi';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Alert,
    Paper,
    OutlinedInput,
    Chip,
    CircularProgress
} from '@mui/material';

const UserRegister = () => {
    const navigate = useNavigate();
    const { register, isAuthenticated } = useBookMyOpAuth();
    const [specialties, setSpecialties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        mobile_number: '',
        date_of_birth: '',
        gender: '',
        address: '',
        preferred_specialties: [],
        has_medical_insurance: false,
        insurance_details: {}
    });

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/bookmyop/dashboard');
        }
        
        const fetchSpecialties = async () => {
            try {
                const response = await BookMyOpAPI.getSpecialties();
                setSpecialties(response.results || []);
            } catch (error) {
                console.error('Failed to fetch specialties:', error);
            }
        };
        
        fetchSpecialties();
    }, [isAuthenticated, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSpecialtiesChange = (event) => {
        const {
            target: { value },
        } = event;
        setFormData(prev => ({
            ...prev,
            preferred_specialties: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            setLoading(false);
            return;
        }

        try {
            const { confirmPassword, ...registrationData } = formData;
            await register(registrationData);
            navigate('/bookmyop/dashboard');
        } catch (err) {
            setError(err.message || 'Registration failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 4
                }}
            >
                <Typography component="h1" variant="h4" gutterBottom>
                    Register for BookMyOP
                </Typography>

                <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleChange}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            value={formData.password}
                            onChange={handleChange}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            id="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="name"
                            label="Full Name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="mobile_number"
                            label="Mobile Number"
                            id="mobile_number"
                            value={formData.mobile_number}
                            onChange={handleChange}
                        />

                        <TextField
                            margin="normal"
                            fullWidth
                            name="date_of_birth"
                            label="Date of Birth"
                            type="date"
                            id="date_of_birth"
                            value={formData.date_of_birth}
                            onChange={handleChange}
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel id="gender-label">Gender</InputLabel>
                            <Select
                                labelId="gender-label"
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                label="Gender"
                            >
                                <MenuItem value="">
                                    <em>Select Gender</em>
                                </MenuItem>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            margin="normal"
                            fullWidth
                            name="address"
                            label="Address"
                            id="address"
                            multiline
                            rows={3}
                            value={formData.address}
                            onChange={handleChange}
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel id="specialties-label">Preferred Specialties</InputLabel>
                            <Select
                                labelId="specialties-label"
                                id="preferred_specialties"
                                multiple
                                value={formData.preferred_specialties}
                                onChange={handleSpecialtiesChange}
                                input={<OutlinedInput label="Preferred Specialties" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => {
                                            const specialty = specialties.find(s => s.id === value);
                                            return (
                                                <Chip 
                                                    key={value} 
                                                    label={specialty ? specialty.name : value} 
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {specialties.map((specialty) => (
                                    <MenuItem key={specialty.id} value={specialty.id}>
                                        {specialty.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="has_medical_insurance"
                                    checked={formData.has_medical_insurance}
                                    onChange={handleChange}
                                    color="primary"
                                />
                            }
                            label="I have medical insurance"
                            sx={{ mt: 2 }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                            disabled={loading}
                        >
                            {loading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Register'
                            )}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Container>
    );
};

export default UserRegister;
